import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { PayrollCenterView } from './PayrollCenterView.js';
import { usePayrollQuery } from './usePayrollQuery.js';

/**
 * The `/payroll` route component (Payroll Center — the most sensitive, money-committing screen, cut to
 * the SPA behind `subsystem-payroll-v2`). Reads the typed `week` search param, fires the READ-ONLY
 * coarse payroll query, and renders the faithful `PayrollCenterView` on success. Loading → accessible
 * a page-shaped `RouteSkeleton`; error → announced error state with a retry that refetches (the money-screens 3-state
 * contract, verbatim tokens).
 *
 * The server computes the gross-pay per employee via the golden calc; this component never
 * re-aggregates and never sends a tenant id (SEC-11). It never recomputes the OT / lunch / ÷52 math —
 * that stays server-side (D-03 / T-22-payroll).
 */

const routeApi = getRouteApi('/payroll');

export function PayrollCenterRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = usePayrollQuery(search);

  if (query.isPending) {
    return <RouteSkeleton label={t('payroll:title')} rows={6} columns={4} filters={2} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('payroll:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('payroll:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('payroll:error.retry')}
        </button>
      </section>
    );
  }

  return <PayrollCenterView data={query.data} search={search} />;
}
